import { Paper, SegmentedControl, Stack, TagsInput, Text } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { IconBellRinging } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { HorizontalField } from '@/components/ui-shared/HorizontalField/HorizontalField'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { MILESTONE_DEFAULT_TRIGGER_STATE } from '../vlmPipelineConfig'
import { MilestoneServerPicker } from './MilestoneServerPicker'
import { PipelineFormValues } from './VlmPipelineForm'
import { MilestoneCameraSelection } from './cameras/MilestoneCameraSelection'

const PREROLL_SECONDS_OPTIONS = [5, 10, 15]
const POSTROLL_SECONDS_OPTIONS = [0, 5, 10, 15]

const toSecondsData = (options: number[]) =>
  options.map((seconds) => ({ label: `${seconds}s`, value: String(seconds) }))

type MilestoneSourceSettingsProps = {
  form: UseFormReturnType<PipelineFormValues>
}

export const MilestoneSourceSettings = ({
  form
}: MilestoneSourceSettingsProps) => {
  const intl = useIntl()
  const { deviceId } = form.values
  const { milestoneServerId } = form.values.source

  const handleServerChange = (serverId: string) => {
    form.setFieldValue('source.milestoneServerId', serverId)
    if (serverId !== milestoneServerId)
      form.setFieldValue('source.milestoneCameras', [])
  }

  return (
    <Stack gap="lg">
      <MilestoneServerPicker
        value={milestoneServerId}
        label={<FormattedMessage id="vlm.pipelines.create.milestone.label" />}
        description={
          <FormattedMessage id="vlm.pipelines.create.milestone.description" />
        }
        error={form.errors['source.milestoneServerId']}
        deviceId={deviceId}
        skeletonCount={6}
        onChange={handleServerChange}
      />

      <Paper p="lg" withBorder>
        <Stack gap="xl">
          <TagsInput
            label={intl.formatMessage({
              id: 'vlm.pipelines.create.milestone.triggerStates.label'
            })}
            description={intl.formatMessage({
              id: 'vlm.pipelines.create.milestone.triggerStates.description'
            })}
            placeholder={intl.formatMessage({
              id: 'vlm.pipelines.create.milestone.triggerStates.placeholder'
            })}
            data={[MILESTONE_DEFAULT_TRIGGER_STATE]}
            leftSection={<IconBellRinging size={16} />}
            styles={(theme) => ({
              pill: {
                backgroundColor: theme.colors['brand-primary'][6],
                color: theme.white
              }
            })}
            {...form.getInputProps('source.triggerStates')}
          />

          {deviceId && milestoneServerId && (
            <MilestoneCameraSelection
              serverId={milestoneServerId}
              deviceId={deviceId}
              value={form.values.source.milestoneCameras}
              error={form.errors['source.milestoneCameras']}
              description={
                <Text size="sm" c="dimmed">
                  <FormattedMessage id="vlm.pipelines.create.milestone.cameras.description" />
                </Text>
              }
              emptySelectionLabel={
                <FormattedMessage id="vlm.pipelines.create.milestone.cameras.all" />
              }
              onChange={(cameras) =>
                form.setFieldValue('source.milestoneCameras', cameras)
              }
            />
          )}

          <HorizontalField
            size="sm"
            label={
              <FormattedMessage id="vlm.pipelines.create.milestone.preroll.label" />
            }
            description={
              <FormattedMessage id="vlm.pipelines.create.milestone.preroll.description" />
            }
          >
            <SegmentedControl
              size="xs"
              color="brand-primary"
              miw={200}
              data={toSecondsData(PREROLL_SECONDS_OPTIONS)}
              value={String(form.values.source.prerollSeconds)}
              onChange={(value) =>
                form.setFieldValue('source.prerollSeconds', Number(value))
              }
            />
          </HorizontalField>

          <HorizontalField
            size="sm"
            label={
              <FormattedMessage id="vlm.pipelines.create.milestone.postroll.label" />
            }
            description={
              <FormattedMessage id="vlm.pipelines.create.milestone.postroll.description" />
            }
          >
            <SegmentedControl
              size="xs"
              color="brand-primary"
              miw={200}
              data={toSecondsData(POSTROLL_SECONDS_OPTIONS)}
              value={String(form.values.source.postrollSeconds)}
              onChange={(value) =>
                form.setFieldValue('source.postrollSeconds', Number(value))
              }
            />
          </HorizontalField>

          <PlatformAlert variant="info" compact>
            <FormattedMessage id="vlm.pipelines.create.milestone.videoWindow.hint" />
          </PlatformAlert>
        </Stack>
      </Paper>
    </Stack>
  )
}
